import React from 'react';
import { AnimatePresence } from 'framer-motion';
import { GlobalOrdersRequest } from '@api/ringil-api.v2.tsx';
import RowButton from '@components/RingilComponents/RowButton.tsx';
import SvgMapPin from '@icons/map-pin.svg?react';
import RouteIssuesMessage from '@app/pages/expedition/create/components/RouteIssuesMessage.tsx';
import RouteStop from '@app/pages/expedition/create/components/RouteStop.tsx';
import { RouteOrderIssue } from '@app/pages/expedition/create/routeRules.ts';
import { OrdersConfig } from '@components/framework/orders/validation.ts';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Form } from '@hooks/useForm/useForm.tsx';
import { NewExpeditionForm } from '@app/pages/expedition/create/types.ts';

interface ComponentProps {
  form: Form<NewExpeditionForm>;
  ordersConfig: OrdersConfig;
  companyId: string;
  onAddStopAbove: (index: number) => void;
  onAddStopAtEnd: () => void;
  onDeleteLocation: (locIndex: number) => void;
  /** False once the route is down to its two mandatory stops. */
  canDeleteLocation: boolean;
  /** Folds a stop into another at the same place; only given while merging shipments. */
  onMergeStop?: (sourceIndex: number, targetIndex: number) => void;
  onDeleteUnloading: (locIndex: number) => void;
  getUnloadableOrders: (locIndex: number) => GlobalOrdersRequest[];
  onUnloadOrder: (locIndex: number, orderId: string) => void;
  onOrderAdded: (locIndex: number, orderId: string) => void;
  onMoveStop: (fromIndex: number, toIndex: number) => void;
  onMoveOrderLoading: (orderId: string, targetLocIndex: number) => void;
  /** Orders currently in an impossible position; empty when the route is valid. */
  orderIssues: RouteOrderIssue[];
  /** Suppresses the entry animation on the first paint so a prefilled route does not fly in. */
  animateEntry: boolean;
}

/** The route: every stop, with its location details on the left and its cargo on the right. */
const RouteSection: React.FC<ComponentProps> = ({
                                                  form,
                                                  ordersConfig,
                                                  companyId,
                                                  onAddStopAbove,
                                                  onAddStopAtEnd,
                                                  onDeleteLocation,
                                                  canDeleteLocation,
                                                  onMergeStop,
                                                  onDeleteUnloading,
                                                  getUnloadableOrders,
                                                  onUnloadOrder,
                                                  onOrderAdded,
                                                  onMoveStop,
                                                  onMoveOrderLoading,
                                                  orderIssues,
                                                  animateEntry,
                                                }) => {
  const { tr } = useTranslationLgs();

  return (
    <>
      <RouteIssuesMessage issues={orderIssues} />
      <AnimatePresence>
      {form.values?.locations?.map((loc, locIndex, locArray) => (
        <RouteStop
          key={loc.id}
          form={form}
          location={loc}
          locIndex={locIndex}
          locationCount={locArray.length}
          ordersConfig={ordersConfig}
          companyId={companyId}
          canDeleteLocation={canDeleteLocation}
          onDeleteLocation={onDeleteLocation}
          onMergeStop={onMergeStop}
          onMoveStop={onMoveStop}
          onAddStopAbove={onAddStopAbove}
          onDeleteUnloading={onDeleteUnloading}
          unloadableOrders={getUnloadableOrders(locIndex)}
          onUnloadOrder={onUnloadOrder}
          onOrderAdded={onOrderAdded}
          onMoveOrderLoading={onMoveOrderLoading}
          orderIssues={orderIssues}
          animateEntry={animateEntry}
        />
      ))}
      </AnimatePresence>
      <RowButton
        title={tr('CreateExpedition.zastavkaNaKonci', 'Stop at the end')}
        icon={<SvgMapPin width={24} height={32} className={'text-content-quaternary'} />}
        onClick={onAddStopAtEnd}
        dataTestId={'addStopoverEnd'}
      />
    </>
  );
};

export default RouteSection;